import * as React from "react";
import { Clock } from "lucide-react";

import { cn } from "../../lib/utils.js";
import { Input } from "./input.js";
import { MenuList } from "./menu-list.js";
import { Popover, PopoverAnchor, PopoverContent } from "./popover.js";
import {
  HHMM_PATTERN, bandInRange, clampTo, inRange, minuteOptions, pad, parseTyped, readable,
} from "./time-input.logic.js";

// A time field that looks like every other shadcn Input, with an hour/minute
// panel of our own instead of the browser's. `<input type="time">` draws a
// native popup Chrome styles itself -- grey columns, its own type scale, no
// dark-mode token of ours -- so it was the one control on the page that did not
// belong to the app. Same approach as DateInput: typed entry stays primary and
// the button is the shortcut, except that here the panel is ours, because a
// time popup (unlike the calendar) is small enough to be worth owning.

export interface TimeInputProps {
  id?: string | undefined;
  /** 24-hour "HH:MM". The only format stored or emitted. */
  value: string;
  onChange: (hhmm: string) => void;
  /** Earliest / latest selectable time, inclusive, as "HH:MM". */
  min?: string | undefined;
  max?: string | undefined;
  /** Minute granularity offered in the panel. Typed entry is never restricted. */
  step?: number | undefined;
  disabled?: boolean | undefined;
  placeholder?: string | undefined;
  "aria-label"?: string | undefined;
  className?: string | undefined;
}

function TimeInput({
  id, value, onChange, min, max, step = 5, disabled, placeholder = "HH:MM",
  className, "aria-label": ariaLabel,
}: TimeInputProps) {
  const [open, setOpen] = React.useState(false);
  const [text, setText] = React.useState(value);
  const [editing, setEditing] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const hourCol = React.useRef<HTMLDivElement>(null);
  const minuteCol = React.useRef<HTMLDivElement>(null);

  // While they are typing, what they typed wins; otherwise the field mirrors the
  // value, so a change made in the panel (or by the parent) shows immediately.
  React.useEffect(() => { if (!editing) setText(value); }, [value, editing]);

  const current = HHMM_PATTERN.test(value) ? value : null;
  const invalid = text.trim() !== "" && parseTyped(text) === null;

  const range = React.useMemo(() => ({ min, max }), [min, max]);
  const allowed = React.useCallback((hhmm: string) => inRange(hhmm, range), [range]);

  function commit(raw: string) {
    const parsed = parseTyped(raw);
    // An unreadable or out-of-range entry reverts rather than guessing: guessing
    // here would quietly file a shift nobody asked for.
    if (parsed && allowed(parsed)) onChange(parsed);
    setText(parsed && allowed(parsed) ? parsed : value);
    setEditing(false);
  }

  function pick(hhmm: string) {
    setEditing(false);
    setText(hhmm);
    onChange(hhmm);
  }

  const minutes = React.useMemo(() => minuteOptions(step, current), [step, current]);

  // Centre both columns on what is selected when the panel opens -- hour 18 is
  // otherwise below the fold and the panel looks like it starts at midnight.
  React.useEffect(() => {
    if (!open) return;
    for (const col of [hourCol.current, minuteCol.current]) {
      const sel = col?.querySelector<HTMLElement>("[data-current='true']");
      if (col && sel) col.scrollTop = sel.offsetTop - col.clientHeight / 2 + sel.clientHeight / 2;
    }
  }, [open]);

  const colClass = "no-scrollbar max-h-[200px] w-14 overflow-y-auto p-1";
  const cellClass = "h-7 shrink-0 rounded-[var(--radius-md)] text-center font-mono text-[13px] tabular-nums transition-colors disabled:cursor-not-allowed disabled:opacity-35";

  return (
    <Popover open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
      <PopoverAnchor asChild>
        <div className={cn("relative w-full", className)}>
          <Input
            id={id}
            ref={inputRef}
            type="text"
            inputMode="numeric"
            autoComplete="off"
            placeholder={placeholder}
            value={text}
            disabled={disabled}
            aria-label={ariaLabel}
            aria-invalid={invalid || undefined}
            onChange={(e) => { setEditing(true); setText(e.target.value); }}
            onFocus={(e) => e.currentTarget.select()}
            onBlur={(e) => commit(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { commit(text); setOpen(false); }
              if (e.key === "Escape" && open) { e.stopPropagation(); setOpen(false); }
              if (e.key === "ArrowDown" && !open) { e.preventDefault(); setOpen(true); }
            }}
            className={cn(
              "pr-14 font-mono tabular-nums",
              invalid && "border-[var(--color-error)] focus:border-[var(--color-error)] focus:ring-[var(--color-error)]",
            )}
          />
          {/* The value is 24-hour everywhere in the app; this says which half of
              the day that is, so nobody has to do the arithmetic to be sure. */}
          {current && (
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-0 right-8 flex items-center text-[10px] font-semibold uppercase tracking-wide text-surface-400"
            >
              {current < "12:00" ? "AM" : "PM"}
            </span>
          )}
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            disabled={disabled}
            tabIndex={-1}
            aria-label="Choose a time"
            className="absolute inset-y-0 right-0 flex w-9 cursor-pointer items-center justify-center rounded-r-md text-surface-400 transition-colors hover:text-surface-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Clock size={15} />
          </button>
        </div>
      </PopoverAnchor>

      <PopoverContent
        align="start"
        className="w-auto p-0"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => { e.preventDefault(); inputRef.current?.focus(); }}
      >
        <div className="flex items-baseline justify-between gap-6 border-b border-surface-100 px-3 py-2">
          <span className="font-mono text-[15px] font-semibold tabular-nums text-surface-900">
            {current ?? "--:--"}
          </span>
          <span className="text-[11px] font-medium text-surface-400">
            {current ? readable(current) : "24-hour"}
          </span>
        </div>

        <div className="flex">
          <div className="flex flex-col items-center border-r border-surface-100">
            <span className="w-full px-1 pt-1.5 text-center text-[10px] font-semibold uppercase tracking-wide text-surface-400">Hr</span>
            <div ref={hourCol} className={colClass}>
            <MenuList className="flex flex-col gap-0.5" role="listbox" aria-label="Hour">
              {Array.from({ length: 24 }, (_, h) => {
                const next = `${pad(h)}:${current?.slice(3) ?? "00"}`;
                const isCurrent = current?.slice(0, 2) === pad(h);
                return (
                  <button
                    key={h}
                    type="button"
                    data-menu-item=""
                    data-current={isCurrent || undefined}
                    role="option"
                    aria-selected={isCurrent}
                    disabled={!bandInRange(`${pad(h)}:00`, `${pad(h)}:59`, range)}
                    onClick={() => pick(clampTo(next, range))}
                    className={cn(cellClass, isCurrent ? "bg-brand-600 font-semibold text-white" : "text-surface-700")}
                  >
                    {pad(h)}
                  </button>
                );
              })}
            </MenuList>
            </div>
          </div>

          <div className="flex flex-col items-center">
            <span className="w-full px-1 pt-1.5 text-center text-[10px] font-semibold uppercase tracking-wide text-surface-400">Min</span>
            <div ref={minuteCol} className={colClass}>
            <MenuList className="flex flex-col gap-0.5" role="listbox" aria-label="Minute">
              {minutes.map((m) => {
                const next = `${current?.slice(0, 2) ?? "00"}:${pad(m)}`;
                const isCurrent = current?.slice(3) === pad(m);
                return (
                  <button
                    key={m}
                    type="button"
                    data-menu-item=""
                    data-current={isCurrent || undefined}
                    role="option"
                    aria-selected={isCurrent}
                    disabled={!allowed(next)}
                    onClick={() => { pick(next); setOpen(false); }}
                    className={cn(cellClass, isCurrent ? "bg-brand-600 font-semibold text-white" : "text-surface-700")}
                  >
                    {pad(m)}
                  </button>
                );
              })}
            </MenuList>
            </div>
          </div>

          {/* Half of the day, as its own column: picking "PM" against hour 06 is
              how most people say 18:00, and it saves counting up from 12. */}
          <div className="flex flex-col items-center border-l border-surface-100">
            <span className="w-full px-1 pt-1.5 text-center text-[10px] font-semibold uppercase tracking-wide text-surface-400">Am/Pm</span>
            <MenuList className="flex flex-col gap-0.5 p-1" role="listbox" aria-label="AM or PM">
              {(["AM", "PM"] as const).map((half) => {
                const h = current ? Number(current.slice(0, 2)) : 0;
                const next = `${pad((h % 12) + (half === "PM" ? 12 : 0))}:${current?.slice(3) ?? "00"}`;
                const isCurrent = current ? (h < 12 ? "AM" : "PM") === half : false;
                return (
                  <button
                    key={half}
                    type="button"
                    data-menu-item=""
                    data-current={isCurrent || undefined}
                    role="option"
                    aria-selected={isCurrent}
                    disabled={!bandInRange(half === "AM" ? "00:00" : "12:00", half === "AM" ? "11:59" : "23:59", range)}
                    onClick={() => pick(clampTo(next, range))}
                    className={cn(cellClass, "w-10", isCurrent ? "bg-brand-600 font-semibold text-white" : "text-surface-700")}
                  >
                    {half}
                  </button>
                );
              })}
            </MenuList>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export { TimeInput };
